import React from 'react';
import {Platform, useWindowDimensions, View} from 'react-native';
import {useSelector} from 'react-redux';
import {get, noop} from 'lodash';

import {BonusProgressBarCSB} from '/component/base/BonusProgressBar';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {DashboardID} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './CSBPlusBanner.style';
import {CSBPlusBannerProps} from './CSBPlusBanner.types';
import {generateOnLearnMore} from './CSBPlusBanner.util';

export const CSBPlusBanner: React.FC<CSBPlusBannerProps> = ({
  currentScreen = '',
  newOrderingEntities = 0,
  currentDate = '',
  popoverShow = noop,
  cardNumber = null,
  totalCards = null,
  bannerHeight,
  handleBannerHeight = noop,
  bannerText = {CSBText: ''},
  newOECount = 0,
  reactivatedOECount = 0,
  totalOECount = 0,
}) => {
  const {fontScale} = useWindowDimensions();
  const isLargeFont = fontScale > 1.15;
  const {tt} = useTranslation('CSBPlusBanner');
  const featureFlag = useSelector(variablesFeatureFlag);
  const showUpdatedCSBPlus = get(featureFlag, 'showUpdatedCSBPlus', false);
  const showEnhancedCSBPlus = get(featureFlag, 'showEnhancedCSBPlus', false);
  const showCsbPlusInsightsFiltering = shouldReleaseFeature(
    get(featureFlag, 'ff_csb_plus_insights_filtering', ''),
  );

  const CSBPlusDetails = bannerText.CSBPlusDetails;
  const csbText = showCsbPlusInsightsFiltering
    ? CSBPlusDetails?.CSBPV5Text ||
      'Each month, you have the opportunity to earn a bonus of 3%, 5%, 7%, or even 10% in FCV by personally welcoming 10 new qualified clients who meet all the necessary criteria and each have a minimum of 275 PQV.'
    : showEnhancedCSBPlus
    ? CSBPlusDetails?.enhancedCSBText ||
      'Earn up to 7% bonus FCV each month by welcoming 7 qualified clients who meet the criteria and have at least 150 PQV (At least 2 clients must be new).'
    : showUpdatedCSBPlus
    ? CSBPlusDetails?.newCSBText ||
      'Earn up to 7% bonus FCV each by having 7 New qualified Clients who meet the criteria and have at least 150 PQV.'
    : CSBPlusDetails?.origCSBText ||
      'Earn up to 5% bonus FCV each month, December 2023 – May 2024, by having 5 Clients who meet the criteria and have at least 150 PQV.';

  const bannerURL =
    CSBPlusDetails?.bannerURL ||
    'https://coachanswers.optavia.com/help/client-support-bonus-plus';

  const renderDescription = () => {
    return (
      <>
        {tt('content', () => {
          return (
            <View
              accessible={true}
              accessibilityLabel={csbText}
              style={styles.descriptionWrapper}>
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                {...testProperties(DashboardID.dashboardCsbBannerDescription)}
                accessibilityRole="text"
                accessible={true}
                style={{position: 'relative'}}>
                {csbText}
              </Text>
            </View>
          );
        })}

        {tt('learnMore', () => {
          return (
            <TouchableOpacity
              accessible={true}
              accessibilityLabel="Learn more about Client Support Bonus+"
              onPress={generateOnLearnMore(
                {popoverShow},
                currentScreen,
                bannerURL,
              )}>
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                style={styles.link}
                isBold
                accessible={true}
                accessibilityRole="link">
                Learn more
              </Text>
            </TouchableOpacity>
          );
        })}
      </>
    );
  };

  return (
    <View
      onLayout={handleBannerHeight}
      style={{
        ...styles.CSBContent,
        ...(bannerHeight
          ? {
              minHeight: bannerHeight,
              ...(Platform.OS !== 'web' ? {height: bannerHeight} : {}),
            }
          : {}),
      }}
      {...testProperties(DashboardID.dashboardCsbBanner)}>
      <View
        style={[
          styles.headerContainer,
          isLargeFont && styles.headerContainerLargeFont,
        ]}
        accessible={true}
        // accessibilityRole="header"
        accessibilityLabel={`Client Support Bonus+ heading. slide ${cardNumber} of ${totalCards}`}>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          accessible={true}
          accessibilityRole="header"
          variant="heading"
          style={styles.headerText}
          {...testProperties(DashboardID.dashboardCsbBannerTitle)}>
          {tt('heading', 'Client Support Bonus+')}
        </Text>

        {totalCards &&
          cardNumber &&
          tt('sideTitle', () => (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              variant="button"
              style={[styles.sideText, isLargeFont && styles.sideTextLargeFont]}
              {...testProperties(DashboardID.dashboardCsbBannerSideTitle)}
              accessible={true}
              accessibilityLabel={`slide ${cardNumber} of ${totalCards}`}>
              {cardNumber} of {totalCards}
            </Text>
          ))}
      </View>

      {renderDescription()}
      <View style={styles.divider} />
      <BonusProgressBarCSB
        newOrderingEntities={newOrderingEntities}
        currentDate={currentDate}
        isBanner={true}
        isDasboard={true}
        popoverShow={popoverShow}
        currentScreen="Dashboard"
        reactivatedOECount={reactivatedOECount}
        newOECount={newOECount}
        totalOECount={totalOECount}
        earnedBonusAmount={''}
        isEarnedBonusCard={false}
      />
    </View>
  );
};
